/**
 * /verify/insurability-pack/:id — the public verify page for a signed insurability pack (V111-5, ADR-0094 §5;
 * v11.6 V116-4-4/4-5, ADR-0114 design round). PUBLIC, no sign-in. It reads the same JSON a machine gets
 * (`GET /verify/insurability-pack/:id?format=json`).
 *
 *  - Keyless (the plain URL): the signature and the version state only. No contents, no names.
 *  - Keyed (a live access session from an insurer or broker link; the server swaps `?k=` for an HttpOnly cookie):
 *    the signed contents, minus internal-only fields, with "Mapping by GAC Pro · not a legal opinion".
 *  - Replaced / withdrawn: a state bar that is a heading with ONE action; the state leads `document.title`; on a
 *    phone the stale contents sit collapsed behind "Show version N as signed".
 *  - Print: a dated "Status checked … when printed · current status: <keyless URL>" line, the state in the page
 *    margin (with a last-page fallback) and "Browser copy" (the URL is in the bottom margin).
 * If `?k=` is still in the address bar (the server normally removes it first), the page sends it once and rewrites
 * the URL with `history.replaceState`, so print footers and share sheets never carry a working key.
 */
import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Alert, BoardroomMode, Brand, Button, Card, Spinner } from '@aigovops/design-system';
import { verifyHeadline } from './VerifyInfraPlan';
import { fetchPackVerify, packVerifyPdfUrl, type PackVerifyResponse } from '../lib/api';
import { fmtDate, plural } from '../lib/format';
import { isOverdue } from '../lib/pack-legal';
import { LegalSection } from '../components/insurability/LegalSection';
import { RECALL_TEXT } from '../components/insurability/PackSharing';
import '../components/programs/strategy-compare.css';
import '../components/programs/infra-inventory.css';
import '../components/programs/plan-view.css';
import '../components/insurability/insurability-pack.css';

const STATE_LABEL: Record<PackVerifyResponse['state'], string> = { current: 'Latest', replaced: 'Replaced', withdrawn: 'Withdrawn' };

const fmtTime = (d: Date) =>
  `${d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })} ${d.toISOString().slice(11, 16)} UTC`;

/** Take `?k=` out of the address bar once (fallback for the server's swap); returns the key it removed. */
function takeKeyFromUrl(): string | null {
  if (typeof window === 'undefined') return null;
  const url = new URL(window.location.href);
  const key = url.searchParams.get('k');
  if (key === null) return null;
  url.searchParams.delete('k');
  window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
  return key;
}

const isNarrow = () => typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(max-width: 859px)').matches;

export function VerifyInsurabilityPack() {
  const { id = '' } = useParams();
  const keyRef = useRef<string | null | undefined>(undefined);
  if (keyRef.current === undefined) keyRef.current = takeKeyFromUrl();
  const q = useQuery({
    queryKey: ['verify-insurability-pack', id],
    queryFn: () => { const k = keyRef.current; keyRef.current = null; return fetchPackVerify(id, k); },
    retry: false,
  });
  const [checkedAt, setCheckedAt] = useState(() => new Date());
  useEffect(() => { if (q.dataUpdatedAt) setCheckedAt(new Date(q.dataUpdatedAt)); }, [q.dataUpdatedAt]);
  // Paper is time-bound: re-stamp "Checked" when printing, and print the collapsed contents too.
  useEffect(() => {
    const before = () => {
      setCheckedAt(new Date());
      document.querySelectorAll<HTMLDetailsElement>('.gp-verify-page details').forEach((d) => { d.open = true; });
    };
    window.addEventListener('beforeprint', before);
    return () => window.removeEventListener('beforeprint', before);
  }, []);

  const d = q.data ?? null;
  // A11Y-T4-04: after moving to another version ("View version N"), focus its heading.
  const shownId = useRef<string | null>(null);
  useEffect(() => {
    if (!d) return;
    if (shownId.current !== null && shownId.current !== id) document.getElementById('ip-h1')?.focus();
    shownId.current = id;
  }, [d, id]);
  const keyless = typeof window !== 'undefined' ? `${window.location.origin}/verify/insurability-pack/${encodeURIComponent(id)}` : `/verify/insurability-pack/${id}`;
  const org = d?.content?.cover.organization_name ?? null;

  useEffect(() => {
    if (!d) { document.title = 'Signed AI insurability pack · GAC Pro'; return; }
    const v = d.version != null ? `version ${d.version}` : 'signed pack';
    const lead = d.state === 'current' ? '' : `${STATE_LABEL[d.state]} · `;
    document.title = d.access
      ? `${lead}${org ?? 'AI insurability pack'} · AI insurability pack · ${v} · GAC Pro`
      : `${lead || 'Signature check · '}AI insurability pack · ${v} · GAC Pro`;
  }, [d, org]);

  return (
    <BoardroomMode>
      <div className="gp-verify-page">
        {d ? <PageBox d={d} keyless={keyless} checkedAt={checkedAt} org={org} /> : null}
        <header className="gp-verify-head">
          <div className="gp-verify-inner">
            <Link to="/" className="gp-verify-brand"><Brand variant="glyph" /><Brand variant="wordmark" /></Link>
          </div>
        </header>
        <main className="gp-verify-inner gp-verify-main" style={{ display: 'grid', gap: 'var(--space-4)' }}>
          {q.isLoading ? <Card><Spinner label="Checking the signature" /></Card> : q.error ? (
            <Card><Alert variant="error"><p>The signature can’t be checked right now.</p><Button compact onClick={() => void q.refetch()}>Try again</Button></Alert></Card>
          ) : !d ? (
            <>
              <h1 className="gp-card-h">Signed AI insurability pack</h1>
              <Card><Alert variant="warning"><p>No signed pack has this link. Check that the whole link was copied.</p></Alert></Card>
            </>
          ) : d.access && d.content ? (
            <Keyed d={d} keyless={keyless} checkedAt={checkedAt} org={org ?? 'AI insurability pack'} />
          ) : (
            <Keyless d={d} keyless={keyless} checkedAt={checkedAt} />
          )}
          <p className="ip-print-only ip-dim" data-testid="ip-print-foot">Browser copy · check the signature and current status at {keyless}</p>
        </main>
      </div>
    </BoardroomMode>
  );
}

/** The state on every printed page (margin boxes); the footer line above is the fallback. */
function PageBox({ d, keyless, checkedAt, org }: { d: PackVerifyResponse; keyless: string; checkedAt: Date; org: string | null }) {
  const st = d.state === 'current' ? `Latest as of ${fmtDate(checkedAt.toISOString())}`
    : d.state === 'replaced' ? `Replaced by version ${d.newer_version?.version ?? ''}`.trim() : 'Withdrawn · do not rely';
  const esc = (s: string) => s.replace(/["\\]/g, '');
  const tail = [org, d.version != null ? `version ${d.version}` : null, keyless].filter(Boolean).join(' · ');
  const font = "font-family: system-ui, -apple-system, 'Segoe UI', Roboto, Arial, sans-serif; font-size: 8pt;";
  return (
    <style data-testid="ip-page-css">{`@page { size: A4; margin: 18mm 14mm 22mm;
  @top-left { content: "Browser copy"; ${font} }
  @bottom-left { content: "${esc(st)} · ${esc(tail)}"; ${font} }
  @bottom-right { content: counter(page) " / " counter(pages); ${font} } }`}</style>
  );
}

function PrintStatus({ checkedAt, keyless }: { checkedAt: Date; keyless: string }) {
  return <p className="ip-print-only ip-dim" data-testid="ip-print-status">Status checked {fmtTime(checkedAt)} when printed · current status: {keyless}</p>;
}

function SignatureDetails({ d, keyless }: { d: PackVerifyResponse; keyless: string }) {
  return (
    <details className="gp-cb-why">
      <summary>Verify signature</summary>
      <dl className="gp-inv-src">
        <div><dt>Package</dt><dd>{d.package_id}{d.version ? ` · version ${d.version}` : ''}</dd></div>
        <div><dt>Signed at</dt><dd>{d.signed_at ?? 'not recorded'}</dd></div>
        <div><dt>Content digest (SHA-256)</dt><dd>{d.content_sha256 ?? 'none'}</dd></div>
        <div><dt>Signature valid</dt><dd>{d.signature_valid ? 'Yes' : 'No'}</dd></div>
        <div><dt>Content matches the digest</dt><dd>{d.content_matches_digest ? 'Yes' : 'No'}</dd></div>
        {d.withdrawal ? <div><dt>Withdrawal record</dt><dd>{d.withdrawal.signature_valid ? 'Signed · valid' : 'Signature not valid'}</dd></div> : null}
        <div><dt>Not covered</dt><dd>The “Checked” line: the version state and signature result are checked live</dd></div>
        <div><dt>Check it yourself</dt><dd><code>gac verify {keyless}</code></dd></div>
      </dl>
    </details>
  );
}

function Keyless({ d, keyless, checkedAt }: { d: PackVerifyResponse; keyless: string; checkedAt: Date }) {
  const h = verifyHeadline({ ...d, plan_version_sha256: null });
  return (
    <>
      <h1 className="gp-card-h" id="ip-h1" tabIndex={-1}>Signed AI insurability pack</h1>
      <Card>
        {d.state !== 'withdrawn' && d.newer_version ? (
          <Alert variant="warning"><p><b>Replaced by version {d.newer_version.version}</b>, signed {fmtDate(d.newer_version.signed_at)}. Ask the sender for the latest version.</p></Alert>
        ) : null}
        {d.state === 'withdrawn' ? (
          <Alert variant="warning"><p><b>Withdrawn{d.withdrawn_at ? ` on ${fmtDate(d.withdrawn_at)}` : ''}</b> · do not rely on this version. Ask the sender for the current pack.</p></Alert>
        ) : null}
        <p><b>{h.text}</b> <span className="ip-chip" data-s={d.state === 'withdrawn' ? 'fail' : 'neutral'}>{STATE_LABEL[d.state] ?? (d.newer_version ? 'Replaced' : 'Latest')}</span></p>
        {!h.ok ? <Alert variant="warning"><p>The pack may have been changed after it was signed. Ask the sender for a new link.</p></Alert> : null}
        <p className="ip-live ip-screen-only" data-testid="ip-checked">Checked {fmtTime(checkedAt)}</p>
        <PrintStatus checkedAt={checkedAt} keyless={keyless} />
        <p className="ip-muted">Contents show only through a link from the sender.</p>
        <p className="ip-muted" data-testid="ip-returning">Opened this before? Open the link from the email again; access lasts an hour per visit.</p>
        <SignatureDetails d={d} keyless={keyless} />
      </Card>
    </>
  );
}

function Keyed({ d, keyless, checkedAt, org }: { d: PackVerifyResponse; keyless: string; checkedAt: Date; org: string }) {
  const doc = d.content!;
  const legal = doc.legal ?? null;
  const stale = d.state !== 'current';
  const [narrow] = useState(isNarrow);
  const [showStale, setShowStale] = useState(false);
  const v = d.version ?? 0;
  const access = d.access!;
  const systemNames = new Map(doc.systems.map((s) => [s.system_id, s.name]));
  // A passed fix-by is a LIVE fact on the Checked line; the signed date in the body is unchanged.
  const lapsed = !stale && legal ? legal.rows.filter((r) => r.gap && r.counted && isOverdue(r.gap.fix_by, checkedAt)) : [];
  const newerHref = d.newer_version?.package_id ? `/verify/insurability-pack/${encodeURIComponent(d.newer_version.package_id)}` : null;
  const pdf = <a href={packVerifyPdfUrl(d.package_id)} download>{stale ? `Download ${STATE_LABEL[d.state].toLowerCase()} PDF` : 'Download PDF'}</a>;
  const mail = `mailto:?subject=${encodeURIComponent(`Current AI insurability pack (${org})`)}`;

  const body = (
    <>
      {legal ? (
        <LegalSection legal={legal} changes={doc.changes ?? null} audience="verify" systemNames={systemNames} now={checkedAt} headingLevel={stale ? 3 : 2} />
      ) : (
        <p className="ip-muted">This version was signed before the legal section format (/3). Download the signed PDF for its contents.</p>
      )}
    </>
  );

  return (
    <>
      <div style={{ display: 'grid', gap: 'var(--space-1)' }}>
        <p className="ip-dim" style={{ margin: 0 }}>AI insurability pack · version {v}</p>
        <h1 className="ip-h1" id="ip-h1" tabIndex={-1}>{org}</h1>
        <p className="ip-muted ip-flat">
          Signed {fmtDate(d.signed_at)} · <span data-testid="ip-access">{access.kind === 'insurer' ? `Shared with ${access.recipient_name}` : `Broker link for ${access.recipient_name}`}</span>
        </p>
        {!stale ? (
          <>
            <div className="ip-stale-row ip-screen-only">
              <p className="ip-live" data-testid="ip-checked">
                <span>Checked {fmtTime(checkedAt)}</span>
                <span className="ip-chip">Latest version</span>
                <span>{d.signature_valid && d.content_matches_digest ? <><span aria-hidden="true">✓ </span>Signature valid</> : 'Signature does not check out'}</span>
              </p>
              {pdf}
            </div>
            {lapsed.length ? (
              <Alert variant="warning">
                <span data-testid="ip-lapsed">{plural(lapsed.length, 'fix-by date')} passed since signing: {lapsed.map((r) => r.title ?? r.control_id).join(', ')}</span>
              </Alert>
            ) : null}
            <PrintStatus checkedAt={checkedAt} keyless={keyless} />
          </>
        ) : null}
      </div>

      {d.state === 'replaced' ? (
        <section className="ip-state-bar" data-state="replaced" aria-labelledby="ip-sb-h">
          <div>
            <h2 className="ip-state-t" id="ip-sb-h">Replaced by version {d.newer_version?.version}{d.newer_version ? ` on ${fmtDate(d.newer_version.signed_at)}` : ''}</h2>
          </div>
          <div className="ip-state-acts">
            {newerHref ? <Link className="gp-linkbtn btn-primary" to={newerHref}>View version {d.newer_version!.version}</Link> : null}
            {pdf}
          </div>
        </section>
      ) : null}
      {d.state === 'withdrawn' ? (
        <section className="ip-state-bar" data-state="withdrawn" aria-labelledby="ip-sb-h">
          <div>
            <h2 className="ip-state-t" id="ip-sb-h">
              Withdrawn by {org}{d.withdrawal?.withdrawn_by ? ` (${d.withdrawal.withdrawn_by})` : ''} on {fmtDate(d.withdrawal?.withdrawn_at ?? d.withdrawn_at)}
              {d.withdrawal?.reason ? ` · ${d.withdrawal.reason}` : ''} · Do not rely on this version
            </h2>
            <p className="ip-state-s">{RECALL_TEXT}</p>
          </div>
          <div className="ip-state-acts">
            {newerHref
              ? <Link className="gp-linkbtn btn-primary" to={newerHref}>View version {d.newer_version!.version}</Link>
              : <><a className="gp-linkbtn btn-primary" href={mail}>Request the current pack</a><span className="ip-dim">Opens your own email</span></>}
            {pdf}
          </div>
        </section>
      ) : null}
      {stale ? (
        <>
          <p className="ip-live ip-screen-only" data-testid="ip-checked">Checked {fmtTime(checkedAt)} · Signature on version {v}: {d.signature_valid ? 'valid' : 'not valid'}</p>
          <PrintStatus checkedAt={checkedAt} keyless={keyless} />
        </>
      ) : null}

      <Card>
        {stale ? (
          <section aria-labelledby="ip-stale-h" style={{ display: 'grid', gap: 'var(--space-3)' }}>
            <div className="ip-stale-row">
              <h2 className="ip-stale-h" id="ip-stale-h">Version {v} as signed {fmtDate(d.signed_at)} ({STATE_LABEL[d.state].toLowerCase()})</h2>
              {narrow ? (
                <Button variant="ghost" compact aria-expanded={showStale} aria-controls="ip-stale-in" onClick={() => setShowStale((s) => !s)}>
                  {showStale ? 'Hide' : 'Show'} version {v} as signed
                </Button>
              ) : null}
            </div>
            <div id="ip-stale-in" className="ip-stale-in" hidden={narrow && !showStale}>{body}</div>
          </section>
        ) : body}
      </Card>

      <Card><SignatureDetails d={d} keyless={keyless} /></Card>
    </>
  );
}
